import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Typography,
  Alert,
  Space,
  message,
  Dropdown,
  Menu,
} from "antd";
import { PlusOutlined, DownOutlined } from "@ant-design/icons";
import axios from "axios";
import PageHeader from "../Layout/PageHeader";
import { PERMISSIONS } from "../../constants/roles";
import { usePermissions } from "../App/PermissionContext";

const { Option, OptGroup } = Select;

const seededTag = (isSeeded) =>
  isSeeded ? <Tag color="blue">Seeded</Tag> : <Tag color="green">Custom</Tag>;

const groupPermissions = (catalog) => {
  const groups = {};
  (catalog || []).forEach((p) => {
    const g = p.pageGroup || "General";
    if (!groups[g]) groups[g] = [];
    groups[g].push(p);
  });
  // sort items within group by order then key
  Object.keys(groups).forEach((g) => {
    groups[g].sort((a, b) => {
      if ((a.order || 0) !== (b.order || 0)) return (a.order || 0) - (b.order || 0);
      return String(a.key).localeCompare(String(b.key));
    });
  });
  return groups;
};

const Role = () => {
  const { permissions, isAdminTester } = usePermissions();
  const canView = isAdminTester && permissions?.includes(PERMISSIONS.VIEW_ROLES);
  const canManage = permissions?.includes(PERMISSIONS.MANAGE_ROLES);

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);
  const [seededFilter, setSeededFilter] = useState("all");

  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [permGroupFilter, setPermGroupFilter] = useState(undefined);

  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [cloneForm] = Form.useForm();

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const [viewRole, setViewRole] = useState(null);
  const [editRole, setEditRole] = useState(null);
  const [editing, setEditing] = useState(false);
  const [cloneRole, setCloneRole] = useState(null);
  const [cloning, setCloning] = useState(false);

  const fetchCatalog = useCallback(async () => {
    setCatalogLoading(true);
    try {
      const res = await axios.get("/api/rbac/permissions");
      setCatalog(res.data.data || []);
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  const fetchRoles = useCallback(async () => {
    const id = ++reqId.current;
    setLoading(true);
    try {
      const params = {};
      if (seededFilter === "seeded") params.seeded = "true";
      if (seededFilter === "custom") params.seeded = "false";
      const res = await axios.get("/api/rbac/roles", { params });
      if (id !== reqId.current) return;
      setRoles(res.data.data || []);
    } catch (error) {
      if (id !== reqId.current) return;
      message.error(error.response?.data?.message || error.message);
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [seededFilter]);

  useEffect(() => {
    if (canView) {
      fetchCatalog();
    }
  }, [canView, fetchCatalog]);

  useEffect(() => {
    if (canView) {
      fetchRoles();
    }
  }, [canView, fetchRoles]);

  const catalogByKey = useMemo(() => {
    const m = {};
    (catalog || []).forEach((p) => {
      m[p.key] = p;
    });
    return m;
  }, [catalog]);

  const groupedCatalog = useMemo(() => groupPermissions(catalog), [catalog]);

  const openCreate = () => {
    createForm.setFieldsValue({ name: "", permissions: [] });
    setCreateOpen(true);
  };

  const handleCreate = async (values) => {
    setCreating(true);
    try {
      await axios.post("/api/rbac/roles", {
        name: values.name,
        permissions: values.permissions || [],
      });
      message.success("Role created");
      setCreateOpen(false);
      createForm.resetFields();
      fetchRoles();
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (role) => {
    setEditRole(role);
  };

  useEffect(() => {
    if (editRole && (catalog || []).length) {
      editForm.setFieldsValue({ name: editRole.name, permissions: editRole.permissions || [] });
    }
  }, [editRole, catalog]);

  const handleEdit = async (values) => {
    if (!editRole) return;
    setEditing(true);
    try {
      await axios.patch(`/api/rbac/roles/${editRole.id}`, {
        name: values.name,
        permissions: values.permissions || [],
      });
      message.success("Role updated");
      setEditRole(null);
      editForm.resetFields();
      fetchRoles();
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setEditing(false);
    }
  };

  const openClone = (role) => {
    cloneForm.setFieldsValue({ name: `${role.name} (copy)` });
    setCloneRole(role);
  };

  const handleClone = async (values) => {
    if (!cloneRole) return;
    setCloning(true);
    try {
      await axios.post(`/api/rbac/roles/${cloneRole.id}/clone`, {
        name: values.name,
      });
      message.success("Role cloned");
      setCloneRole(null);
      cloneForm.resetFields();
      fetchRoles();
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setCloning(false);
    }
  };

  const handleDelete = async (role) => {
    try {
      await axios.delete(`/api/rbac/roles/${role.id}`);
      message.success("Role deleted");
      fetchRoles();
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    }
  };

  const renderGroupFilter = () => (
    <Select
      placeholder="Filter by group"
      value={permGroupFilter}
      onChange={setPermGroupFilter}
      allowClear
      style={{ width: "100%", marginBottom: 8 }}
    >
      {Object.keys(groupedCatalog)
        .sort()
        .map((g) => (
          <Option key={g} value={g}>
            {g}
          </Option>
        ))}
    </Select>
  );

  const renderPermissionSelect = () => (
    <Select
      mode="multiple"
      showSearch
      placeholder="Select permissions (search by name or group)"
      loading={catalogLoading}
      optionFilterProp="children"
      filterOption={(input, option) => {
        const label = String(option?.children ?? "").toLowerCase();
        const group = String(catalogByKey[option?.value]?.pageGroup ?? "").toLowerCase();
        const q = (input || "").toLowerCase();
        return label.includes(q) || group.includes(q);
      }}
      maxTagCount={20}
      style={{ width: "100%" }}
    >
      {Object.keys(groupedCatalog)
        .sort()
        .filter((g) => !permGroupFilter || g === permGroupFilter)
        .map((group) => (
          <OptGroup key={group} label={group}>
            {groupedCatalog[group].map((p) => (
              <Option key={p.key} value={p.key}>
                {p.label || p.key}
              </Option>
            ))}
          </OptGroup>
        ))}
    </Select>
  );

  const viewPermissionsGrouped = useMemo(() => {
    if (!viewRole) return {};
    const groups = {};
    (viewRole.permissions || []).forEach((key) => {
      const meta = catalogByKey[key] || { pageGroup: "General", label: key };
      const g = meta.pageGroup || "General";
      if (!groups[g]) groups[g] = [];
      groups[g].push(key);
    });
    return groups;
  }, [viewRole, catalogByKey]);

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      render: (code) => code || "-",
    },
    {
      title: "Seeded",
      dataIndex: "isSeeded",
      key: "isSeeded",
      render: (isSeeded) => seededTag(isSeeded),
    },
    {
      title: "Permissions",
      dataIndex: "permissions",
      key: "permissions",
      render: (perms) => (perms || []).length,
    },
    {
      title: "Users",
      dataIndex: "userCount",
      key: "userCount",
      render: (count) => count ?? 0,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, role) => {
        const isSeeded = role.isSeeded === true;
        const inUse = (role.userCount || 0) > 0;

        const menu = (
          <Menu>
            <Menu.Item onClick={() => setViewRole(role)}>View details</Menu.Item>
            <Menu.Item disabled={!canManage} onClick={() => { if (!canManage) return; openClone(role); }}>Clone</Menu.Item>
            <Menu.Item
              disabled={isSeeded || !canManage}
              onClick={() => {
                if (isSeeded || !canManage) return;
                openEdit(role);
              }}
            >
              Edit
            </Menu.Item>
            <Menu.Item
              disabled={isSeeded || inUse || !canManage}
              danger
              onClick={() => {
                if (isSeeded || inUse || !canManage) return;
                Modal.confirm({
                  title: `Delete role "${role.name}"?`,
                  content: "This action cannot be undone.",
                  okText: "Delete",
                  okButtonProps: { danger: true },
                  onOk: () => handleDelete(role),
                });
              }}
            >
              Delete
            </Menu.Item>
          </Menu>
        );

        return (
          <Dropdown overlay={menu} trigger={["click"]}>
            <Button size="small">
              Actions <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  if (!canView) {
    return (
      <>
        <PageHeader title="Roles" childRoutes={[{ path: "/roles", breadcrumbName: "Roles" }]} />
        <Alert type="warning" showIcon message="No access" description="You do not have permission to view roles." />
      </>
    );
  }

  return (
    <>
      <PageHeader title="Roles" childRoutes={[{ path: "/roles", breadcrumbName: "Roles" }]} />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <Space>
          <span>Filter:</span>
          <Select value={seededFilter} onChange={setSeededFilter} style={{ width: 160 }}>
            <Option value="all">All</Option>
            <Option value="seeded">Seeded</Option>
            <Option value="custom">Custom</Option>
          </Select>
        </Space>

        {canManage && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Create role
          </Button>
        )}
      </div>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={roles}
        pagination={false}
      />

      {/* Create modal */}
      <Modal
        title="Create role"
        visible={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => createForm.submit()}
        okText="Create"
        confirmLoading={creating}
        destroyOnClose
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreate}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Name is required" }]}
          >
            <Input placeholder="Role name" />
          </Form.Item>
          {renderGroupFilter()}
          <Form.Item
            name="permissions"
            label="Permissions"
            rules={[{ required: true, message: "Select at least one permission" }]}
          >
            {renderPermissionSelect()}
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit modal */}
      <Modal
        title="Edit role"
        visible={!!editRole}
        onCancel={() => {
          setEditRole(null);
          editForm.resetFields();
        }}
        onOk={() => editForm.submit()}
        okText="Save"
        confirmLoading={editing}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" onFinish={handleEdit}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Name is required" }]}
          >
            <Input placeholder="Role name" />
          </Form.Item>
          {renderGroupFilter()}
          <Form.Item
            name="permissions"
            label="Permissions"
            rules={[{ required: true, message: "Select at least one permission" }]}
          >
            {renderPermissionSelect()}
          </Form.Item>
        </Form>
      </Modal>

      {/* Clone modal */}
      <Modal
        title="Clone role"
        visible={!!cloneRole}
        onCancel={() => {
          setCloneRole(null);
          cloneForm.resetFields();
        }}
        onOk={() => cloneForm.submit()}
        okText="Clone"
        confirmLoading={cloning}
        destroyOnClose
      >
        <Form form={cloneForm} layout="vertical" onFinish={handleClone}>
          <Form.Item
            name="name"
            label="New name"
            rules={[{ required: true, message: "Name is required" }]}
          >
            <Input placeholder="New role name" />
          </Form.Item>
        </Form>
      </Modal>

      {/* View details modal */}
      <Modal
        title={viewRole ? `Role: ${viewRole.name}` : "Role details"}
        visible={!!viewRole}
        onCancel={() => setViewRole(null)}
        footer={[<Button key="close" onClick={() => setViewRole(null)}>Close</Button>]}
        destroyOnClose
      >
        {viewRole && (
          <>
            <p>
              <strong>Code:</strong> {viewRole.code || "-"}
            </p>
            <p>
              <strong>Seeded:</strong> {viewRole.isSeeded ? "Yes" : "No"}
            </p>
            <p>
              <strong>Users:</strong> {viewRole.userCount ?? 0}
            </p>
            <div>
              <strong>Permissions:</strong>
              {Object.keys(viewPermissionsGrouped).length === 0 ? (
                <p>None</p>
              ) : (
                Object.keys(viewPermissionsGrouped)
                  .sort()
                  .map((group) => (
                    <div key={group} style={{ marginTop: 8 }}>
                      <Tag color="default">{group}</Tag>
                      <ul style={{ marginTop: 4 }}>
                        {viewPermissionsGrouped[group].map((key) => (
                          <li key={key}>
                            {catalogByKey[key]?.label || key}
                            {catalogByKey[key]?.description ? (
                              <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                                {catalogByKey[key].description}
                              </Typography.Text>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))
              )}
            </div>
          </>
        )}
      </Modal>
    </>
  );
};

export default Role;
